<template>
  <div v-if="offer">
    <HeaderContent title="Offer Document Detail" />

    <!-- MAIN DETAIL VIEW -->
    <BCard class="mb-4">
      <div
        class="d-flex flex-wrap justify-content-between align-items-center gap-2"
      >
        <div class="mb-2 mb-md-0">
          <BButton
            variant="secondary"
            @click="router.push('/offerdocuments')"
            size="sm"
          >
            <i class="fas fa-arrow-left me-1"></i>
            <span class="d-none d-sm-inline">Back</span>
          </BButton>
        </div>
        <div class="d-flex flex-wrap gap-2 justify-content-end">
          <!-- Status Change Buttons -->
          <BButton
            v-if="showApproveButton"
            variant="success"
            @click="changeStatus('Approved')"
            size="sm"
            title="Approve Offer"
          >
            <i class="fas fa-check-circle me-1"></i>
            <span class="d-none d-sm-inline">Approve</span>
          </BButton>
          <BButton
            v-if="showRejectButton"
            variant="danger"
            @click="changeStatus('Rejected')"
            size="sm"
            title="Reject Offer"
          >
            <i class="fas fa-times-circle me-1"></i>
            <span class="d-none d-sm-inline">Reject</span>
          </BButton>
          <BButton
            v-if="showFinishButton"
            variant="primary"
            @click="changeStatus('Completed')"
            size="sm"
            title="Mark as Completed"
          >
            <i class="fas fa-check me-1"></i>
            <span class="d-none d-sm-inline">Completed</span>
          </BButton>
          <BButton
            v-if="showCancelButton"
            variant="dark"
            @click="changeStatus('Cancelled')"
            size="sm"
            title="Cancel Offer"
          >
            <i class="fas fa-ban me-1"></i>
            <span class="d-none d-sm-inline">Cancel</span>
          </BButton>

          <!-- Payment Button -->
          <BButton
            v-if="showPaymentButton"
            variant="success"
            @click="openPaymentModal"
            size="sm"
            title="Manage Payment"
          >
            <i class="fas fa-credit-card me-1"></i>
            <span class="d-none d-sm-inline">Payment</span>
          </BButton>

          <!-- Other Buttons -->
          <BDropdown variant="info" size="sm" no-caret>
            <template #button-content>
              <i class="fas fa-print me-1"></i>
              <span class="d-none d-sm-inline">Download PDF</span>
              <i class="fas fa-caret-down ms-1"></i>
            </template>
            <BDropdownItem @click="printDocument('package')">
              <i class="fas fa-file-invoice me-2"></i> Tour Package Offer
            </BDropdownItem>
            <BDropdownItem
              v-if="offer.payment_status !== 'Fullpayment'"
              @click="printDocument('deposit')"
            >
              <i class="fas fa-receipt me-2"></i> Prepayment Receipt
            </BDropdownItem>
            <BDropdownItem
              v-if="offer.payment_status === 'Fullpayment'"
              @click="printDocument('full')"
            >
              <i class="fas fa-receipt me-2"></i> Full Payment Receipt
            </BDropdownItem>
          </BDropdown>
          <BButton
            v-if="showEditButton"
            variant="warning"
            @click="router.push(`/offerdocuments/edit/${offer.uuid}`)"
            size="sm"
            title="Edit Offer"
          >
            <i class="fas fa-edit me-1"></i>
            <span class="d-none d-sm-inline">Edit Offer</span>
          </BButton>
        </div>
      </div>
    </BCard>

    <!-- MAIN DETAIL VIEW -->
    <BCard class="mb-4 shadow-sm border-0">
      <div class="p-3" style="font-family: 'Segoe UI', Arial, sans-serif">
        <!-- Header Info -->
        <div class="row mb-4 border-bottom pb-4 px-2 text-left">
          <div class="col-md-3 mb-3">
            <div class="font-weight-bold small text-dark">Offer No</div>
            <div class="mt-1 font-weight-bold text-primary">
              {{ offer.offer_no }}
            </div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="font-weight-bold small text-dark">Guest</div>
            <div class="mt-1">{{ offer.guest?.fullname || "-" }}</div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="font-weight-bold small text-dark">
              Offer Title / Name
            </div>
            <div class="mt-1">{{ offer.name || "-" }}</div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="font-weight-bold small text-dark">Date Period</div>
            <div class="mt-1">{{ formatDatePeriode(offer.programs) }}</div>
          </div>
          <div class="col-md-3 mb-2">
            <div class="font-weight-bold small text-dark">Duration & PAX</div>
            <div class="mt-1">
              {{ offer.duration_days }} Days | {{ offer.pax }} Pax
            </div>
          </div>
          <div class="col-md-3 mb-2">
            <div class="font-weight-bold small text-dark">Currency</div>
            <div class="mt-1">
              {{ offer.currency?.name }} ({{ offer.currency?.symbol }})
            </div>
          </div>
          <div class="col-md-3 mb-2">
            <div class="font-weight-bold small text-dark">Exchange Rate</div>
            <div class="mt-1">
              1 {{ offer.currency?.code }} =
              {{ formatDecimal(offer.exchange_rate) }} IDR
            </div>
          </div>
          <div class="col-md-3 mb-2">
            <div class="font-weight-bold small text-dark">Payment Status</div>
            <div class="mt-2">
              <span :class="paymentStatusBadge(offer.payment_status)">{{
                offer.payment_status || "Unpaid"
              }}</span>
            </div>
          </div>
          <div class="col-md-3 mb-2">
            <div class="font-weight-bold small text-dark">Status</div>
            <div class="mt-2 text-capitalize">
              <span :class="statusBadge(offer.status)">{{ offer.status }}</span>
            </div>
          </div>
          <div class="col-md-3 mb-2" v-if="offer.approved_date">
            <div class="font-weight-bold small text-dark">Approved Date</div>
            <div class="mt-1">{{ formatDate(offer.approved_date) }}</div>
          </div>
          <div class="col-md-3 mb-2" v-if="offer.cancelled_at">
            <div class="font-weight-bold small text-dark">Cancelled Date</div>
            <div class="mt-1 text-danger font-weight-bold">
              {{ formatDate(offer.cancelled_at) }}
            </div>
          </div>
          <div class="col-md-3 mb-2">
            <div class="font-weight-bold small text-dark">Created At</div>
            <div class="mt-1">{{ formatDate(offer.created_at) }}</div>
          </div>
        </div>

        <!-- Cancellation Note Alert -->
        <div
          v-if="offer.status === 'Cancelled' && offer.cancellation_note"
          class="alert alert-danger text-left mb-4 shadow-sm"
        >
          <div class="font-weight-bold mb-1">
            <i class="fas fa-ban me-1"></i> Cancellation Reason
          </div>
          <div style="white-space: pre-wrap">{{ offer.cancellation_note }}</div>
        </div>

        <!-- Program Table -->
        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead style="background-color: #e1eed9">
              <tr>
                <th width="50">Day</th>
                <th>Date</th>
                <th colspan="2">Destination</th>
                <th class="text-left">Accommodation</th>
                <th class="text-left">Activity</th>
                <th class="text-right" width="150">Price (IDR)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(prog, idx) in offer.programs" :key="idx">
                <td class="text-center font-weight-bold">{{ prog.day }}</td>
                <td>{{ formatDateShort(prog.date) }}</td>
                <td class="text-left">
                  <div class="small">{{ prog.to_destination?.name }}</div>
                </td>
                <td class="text-left">
                  <div class="small">{{ prog.from_destination?.name }}</div>
                </td>
                <td class="text-left">
                  <div
                    v-for="acc in prog.accommodations"
                    :key="acc.uuid"
                    class="small"
                  >
                    {{ acc.name }} ({{ acc.room_type }})
                  </div>
                  <span
                    v-if="!prog.accommodations?.length"
                    class="text-muted small"
                    >-</span
                  >
                </td>
                <td class="text-left">
                  <div class="font-weight-bold small">{{ prog.name }}</div>
                  <ul
                    class="mb-0 ps-3 small text-muted"
                    v-if="prog.activities?.length"
                  >
                    <li v-for="act in prog.activities" :key="act.uuid">
                      {{ act.name }}
                      <div style="margin-left: 2px; white-space: pre-wrap">
                        {{ act.description }}
                      </div>
                    </li>
                  </ul>
                  <div class="mt-1 small" v-if="prog.meals?.length">
                    <i class="fas fa-utensils me-1"></i>
                    <span v-for="(m, midx) in prog.meals" :key="m.uuid"
                      >{{ m.name
                      }}{{ midx < prog.meals.length - 1 ? ", " : "" }}</span
                    >
                  </div>
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatRupiah(getDaySubtotal(prog)) }}
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        getDaySubtotal(prog) / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="bg-light font-weight-bold">
                <td colspan="6" class="text-right">Tour Package Price</td>
                <td class="text-right">
                  <div>{{ formatRupiah(programSubtotal) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        programSubtotal / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="autoSingleSupplement > 0">
                <td colspan="6" class="text-right">Single Supplement</td>
                <td class="text-right">
                  <div>{{ formatRupiah(autoSingleSupplement) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        autoSingleSupplement / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="totalAdditional > 0">
                <td colspan="6" class="text-right">Additional Extras</td>
                <td class="text-right">
                  <div>{{ formatRupiah(totalAdditional) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        totalAdditional / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="offer.entrance_fee > 0">
                <td colspan="6" class="text-right">Entrance Fee</td>
                <td class="text-right">
                  <div>{{ formatRupiah(offer.entrance_fee) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        offer.entrance_fee / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="offer.ferry_fee > 0">
                <td colspan="6" class="text-right">Ferry Fee</td>
                <td class="text-right">
                  <div>{{ formatRupiah(offer.ferry_fee) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        offer.ferry_fee / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="offer.toll_fee > 0">
                <td colspan="6" class="text-right">Toll Fee</td>
                <td class="text-right">
                  <div>{{ formatRupiah(offer.toll_fee) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        offer.toll_fee / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="offer.np_permit_fee > 0">
                <td colspan="6" class="text-right">NP Permit Fee</td>
                <td class="text-right">
                  <div>{{ formatRupiah(offer.np_permit_fee) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    {{
                      formatCurrency(
                        offer.np_permit_fee / Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <!-- Keep detailed tax/disc if they exist for completeness, but they might be 0 -->
              <tr v-if="offer.discount_amount > 0">
                <td colspan="6" class="text-right text-danger">
                  Discount
                  <span v-if="offer.discount_type === 'percent'"
                    >({{ offer.discount }}%)</span
                  >
                </td>
                <td class="text-right text-danger">
                  <div>- {{ formatRupiah(offer.discount_amount) }}</div>
                  <div
                    v-if="offer.currency?.code !== 'IDR'"
                    class="small text-muted italic"
                  >
                    -
                    {{
                      formatCurrency(
                        offer.discount_amount /
                          Number(offer.exchange_rate || 1),
                      )
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="offer.service_amount > 0">
                <td colspan="6" class="text-right">
                  Service ({{ offer.service }}%)
                </td>
                <td class="text-right">
                  {{ formatRupiah(offer.service_amount) }}
                </td>
              </tr>
              <tr v-if="offer.tax_amount > 0">
                <td colspan="6" class="text-right">Tax ({{ offer.tax }}%)</td>
                <td class="text-right">{{ formatRupiah(offer.tax_amount) }}</td>
              </tr>
              <tr style="background-color: #f8f9fa; font-size: 1rem">
                <td colspan="6" class="text-right font-weight-bold text-muted">
                  Grand Total (IDR)
                </td>
                <td class="text-right font-weight-bold text-muted">
                  {{ formatRupiah(offer.grand_total) }}
                </td>
              </tr>
              <tr style="background-color: #e1eed9; font-size: 1.1rem">
                <td
                  colspan="6"
                  class="text-right font-weight-bold text-success"
                >
                  Grand Total Sales (IDR)
                </td>
                <td
                  class="text-right font-weight-bold text-white"
                  style="background-color: #489464"
                >
                  {{
                    formatRupiah(offer.grand_total_final || offer.grand_total)
                  }}
                </td>
              </tr>
              <tr
                v-if="offer.currency?.code !== 'IDR'"
                class="bg-primary text-white"
              >
                <td colspan="6" class="text-right font-weight-bold">
                  Grand Total Sales ({{ offer.currency?.code }})
                </td>
                <td
                  class="text-right font-weight-bold"
                  style="background-color: #e1eed9; color: #000 !important"
                >
                  {{ formatCurrency(offer.grand_total_foreign) }}
                </td>
              </tr>
              <tr>
                <td colspan="7" style="height: 40px"></td>
              </tr>
              <tr class="bg-light">
                <td colspan="6" class="text-right">Total Paid</td>
                <td class="text-right bg-success text-white">
                  {{ formatRupiah(offer.paid) }}
                </td>
              </tr>
              <tr class="bg-light">
                <td colspan="6" class="text-right">Remaining</td>
                <td class="text-right bg-danger text-white">
                  {{ formatRupiah(offer.remaining) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Additional Extras Detail Section (Table Format) -->
        <div v-if="offer.additionals?.length" class="mt-4 text-left">
          <h6
            class="font-weight-bold text-success border-bottom pb-2 mb-3 italic"
          >
            <i class="fas fa-plus-circle me-1"></i> Additional Extras Detail
          </h6>
          <div class="table-responsive">
            <table class="table table-bordered table-sm small">
              <thead class="bg-light">
                <tr>
                  <th class="text-left">Item Name & Description</th>
                  <th class="text-center" width="100">Pax</th>
                  <th class="text-right" width="150">Price Per Pax</th>
                  <th class="text-right" width="180">Total Price</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(add, idx) in offer.additionals" :key="idx">
                  <td class="text-left">
                    <div class="font-weight-bold">{{ add.name }}</div>
                    <div
                      class="x-small text-muted italic"
                      v-if="add.description"
                    >
                      {{ add.description }}
                    </div>
                  </td>
                  <td class="text-center">{{ add.pax }}</td>
                  <td class="text-right">
                    {{
                      formatRupiah(
                        Math.round(
                          (add.sub_total || add.price * add.pax) / add.pax,
                        ),
                      )
                    }}
                    <div
                      v-if="offer.currency?.code !== 'IDR'"
                      class="x-small text-muted italic"
                    >
                      {{
                        formatCurrency(
                          Math.round(
                            (add.sub_total || add.price * add.pax) / add.pax,
                          ) / Number(offer.exchange_rate || 1),
                        )
                      }}
                    </div>
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatRupiah(add.sub_total || add.price * add.pax) }}
                    <div
                      v-if="offer.currency?.code !== 'IDR'"
                      class="x-small text-muted italic text-primary"
                    >
                      {{
                        formatCurrency(
                          (add.sub_total || add.price * add.pax) /
                            Number(offer.exchange_rate || 1),
                        )
                      }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Additional Info -->
        <div class="row mt-4 text-left">
          <div class="col-md-6" v-if="offer.inclusion">
            <h6 class="font-weight-bold border-bottom pb-2 mb-2">Inclusions</h6>
            <ul class="ps-3 small">
              <li
                v-for="(line, lidx) in offer.inclusion.split('\n')"
                :key="lidx"
              >
                {{ line }}
              </li>
            </ul>
          </div>
          <div class="col-md-6" v-if="offer.exclusion">
            <h6 class="font-weight-bold border-bottom pb-2 mb-2">Exclusions</h6>
            <ul class="ps-3 small">
              <li
                v-for="(line, lidx) in offer.exclusion.split('\n')"
                :key="lidx"
              >
                {{ line }}
              </li>
            </ul>
          </div>
        </div>

        <div class="mt-4 text-left" v-if="offer.comments">
          <h6 class="font-weight-bold border-bottom pb-2 mb-2">
            Comments or Special Instructions
          </h6>
          <p class="small" style="white-space: pre-wrap">
            {{ offer.comments }}
          </p>
        </div>

        <div class="mt-4 text-left" v-if="offer.term_condition">
          <h6 class="font-weight-bold border-bottom pb-2 mb-2">
            Term & Condition
          </h6>
          <p class="small" style="white-space: pre-wrap">
            {{ offer.term_condition }}
          </p>
        </div>

        <div class="mt-4 text-left" v-if="offer.passanger_info">
          <h6 class="font-weight-bold border-bottom pb-2 mb-2">
            Passenger/Luggage Capacity
          </h6>
          <p class="small" style="white-space: pre-wrap">
            {{ offer.passanger_info }}
          </p>
        </div>
      </div>
    </BCard>
  </div>

  <!-- Payment Modal -->
  <OfferPaymentModal
    v-model:modal="showPaymentModal"
    :offer="offer"
    @refresh="loadDetail"
  />
</template>

<script setup>
import { ref, onMounted, computed, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import OfferPaymentModal from "./OfferPaymentModal.vue";
import {
  formatDate,
  formatDatePeriode,
  formatRupiah,
  formatDecimal,
} from "@/utils/format";
import { BButton, BCard, BDropdown, BDropdownItem } from "bootstrap-vue-next";

const auth = useAuthStore();
const $loading = useLoading();
const router = useRouter();
const route = useRoute();
const swal = inject("$swal");
const toast = useToast();

const offer = ref(null);
const showPaymentModal = ref(false);

// Computed properties for button visibility
const showApproveButton = computed(() => {
  const status = offer.value?.status?.toLowerCase();
  return status === "draft" || status === "pending";
});

const showRejectButton = computed(() => {
  const status = offer.value?.status?.toLowerCase();
  // Only draft and pending can be rejected (NOT approved)
  return status === "draft" || status === "pending";
});

const showFinishButton = computed(() => {
  const status = offer.value?.status?.toLowerCase();
  return status === "approved";
});

const showPaymentButton = computed(() => {
  const status = offer.value?.status?.toLowerCase();
  return status === "approved";
});

const showCancelButton = computed(() => {
  const status = offer.value?.status?.toLowerCase();
  return status !== "cancelled" && status !== "completed";
});

const showEditButton = computed(() => {
  const status = offer.value?.status?.toLowerCase();
  return status === "draft" || status === "pending" || status === "approved";
});

const changeStatus = async (newStatus) => {
  const statusLabels = {
    Approved: "Approve",
    Rejected: "Reject",
    Completed: "Completed",
    Cancelled: "Cancel",
  };

  let payload = { status: newStatus };

  if (newStatus === "Cancelled") {
    const result = await swal({
      title: "Cancel Offer Document",
      text: "Please provide a reason / note for cancelling this offer:",
      input: "textarea",
      inputPlaceholder: "Enter reason / note for cancellation...",
      inputAttributes: {
        "aria-label": "Enter reason for cancellation",
        rows: 3,
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel Offer!",
      cancelButtonText: "Close",
      customClass: {
        popup: "rounded",
        confirmButton: "btn btn-danger me-2",
        cancelButton: "btn btn-secondary",
      },
      buttonsStyling: false,
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Cancellation reason is required!";
        }
      },
    });

    if (!result.value) return;
    payload.cancellation_note = result.value.trim();
  } else {
    const result = await swal({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${statusLabels[
        newStatus
      ].toLowerCase()} this offer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${statusLabels[newStatus]}!`,
      cancelButtonText: "Cancel",
      customClass: {
        popup: "rounded",
        confirmButton: `btn btn-${
          newStatus === "Approved"
            ? "success"
            : newStatus === "Rejected"
            ? "danger"
            : "info"
        } me-2`,
        cancelButton: "btn btn-secondary",
      },
      buttonsStyling: false,
    });

    if (!result.value) return;
  }

  const loader = $loading.show({
    color: "#489464",
    loader: "spinner",
  });

  try {
    const response = await axios.put(
      `${process.env.VUE_APP_API_URL}/offer/status/${route.params.uuid}`,
      payload,
      {
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + auth._token,
        },
      },
    );

    if (response.data.status) {
      toast.success(response.data.message);
      // Reload offer data
      await loadDetail();
    } else {
      toast.error(response.data.message);
    }
  } catch (error) {
    toast.error(
      "Failed to update status: " +
        (error.response?.data?.message || error.message),
    );
  } finally {
    loader.hide();
  }
};

const openPaymentModal = () => {
  showPaymentModal.value = true;
};

const loadDetail = async () => {
  const loader = $loading.show();
  try {
    const response = await axios.get(
      `${process.env.VUE_APP_API_URL}/offer/detail/${route.params.uuid}`,
      {
        headers: { Authorization: "Bearer " + auth._token },
      },
    );
    offer.value = response.data.data;
  } catch (e) {
    console.error(e);
  } finally {
    loader.hide();
  }
};

const statusBadge = (status) => {
  switch (status) {
    case "Approved":
      return "badge badge-success";
    case "Rejected":
      return "badge badge-danger";
    case "Draft":
      return "badge badge-secondary";
    case "Pending":
      return "badge badge-warning";
    case "Completed":
      return "badge badge-primary";
    case "Cancelled":
      return "badge badge-danger";
    default:
      return "badge badge-info";
  }
};

const paymentStatusBadge = (paymentStatus) => {
  switch (paymentStatus?.toLowerCase()) {
    case "fullpayment":
      return "badge badge-success";
    case "dp":
      return "badge badge-warning";
    case "unpaid":
      return "badge badge-secondary";
    default:
      return "badge badge-secondary";
  }
};

const getDaySubtotal = (day) => {
  let total = 0;

  total +=
    day.transportations?.reduce(
      (s, i) => s + Number(i.sub_total || i.price * i.pax),
      0,
    ) || 0;
  // Exclude single supplement items from day subtotal
  total +=
    day.accommodations
      ?.filter((i) => !i.is_single_supplement)
      .reduce(
        (s, i) => s + Number(i.total_price || i.sub_total || i.price * i.pax),
        0,
      ) || 0;
  total +=
    day.activities?.reduce(
      (s, i) => s + Number(i.sub_total || i.price * i.pax),
      0,
    ) || 0;
  total +=
    day.meals?.reduce(
      (s, i) => s + Number(i.sub_total || i.price * (i.pax || offer.value.pax)),
      0,
    ) || 0;

  return total;
};

const programSubtotal = computed(() => {
  if (!offer.value?.programs) return 0;
  return offer.value.programs.reduce(
    (sum, day) => sum + getDaySubtotal(day),
    0,
  );
});

const totalAdditional = computed(() => {
  if (!offer.value?.additionals) return 0;
  return offer.value.additionals.reduce((sum, item) => {
    return (
      sum +
      (Number(item.sub_total) || Number(item.price) * (Number(item.pax) || 1))
    );
  }, 0);
});

const autoSingleSupplement = computed(() => {
  if (!offer.value?.programs) return 0;
  let sum = 0;
  offer.value.programs.forEach((day) => {
    sum +=
      day.accommodations
        ?.filter((i) => i.is_single_supplement)
        .reduce(
          (s, i) => s + Number(i.total_price || i.sub_total || i.price * i.pax),
          0,
        ) || 0;
  });
  return sum;
});

const formatCurrency = (value) => {
  if (!offer.value?.currency) return formatRupiah(value);
  if (offer.value.currency.code === "IDR") return formatRupiah(value);

  // Use Intl.NumberFormat with the currency locale
  const locale = offer.value.currency.locale || "en-US";
  // const formatter = new Intl.NumberFormat(locale, {
  //     style: 'currency',
  //     currency: offer.value.currency.code,
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2
  // });

  // As in Form view, we prioritize the symbol from DB if needed, but formatter is safer for locale correctness.
  // For consistency with Form:
  const numberFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${offer.value.currency.symbol} ${numberFormatter.format(
    Number(value || 0),
  )}`;
};

const formatDateShort = (date) => {
  if (!date) return "-/-";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

// const getDayDateShort = (date) => {
//     if (!date) return '';
//     const d = new Date(date);
//     return d.getDate() + '/' + (d.getMonth() + 1);
// };

const printDocument = async (type = "package") => {
  const loader = $loading.show();
  try {
    const response = await axios.get(
      `${process.env.VUE_APP_API_URL}/offer/print/${route.params.uuid}?type=${type}`,
      {
        headers: { Authorization: "Bearer " + auth._token },
        responseType: "blob",
      },
    );
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" }),
    );
    // Open in new tab for preview/printing
    window.open(url, "_blank");
  } catch (e) {
    console.error("Print error:", e);
    alert("Failed to generate PDF. Please try again.");
  } finally {
    loader.hide();
  }
};

onMounted(() => {
  loadDetail();
});
</script>

<style>
.italic {
  font-style: italic;
}
</style>
